import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import { fetchTradingHistory } from "@/state/slices/tradingSlice";

/** Owner-only one-shot + close-triggered history refetch.
 *
 *  Why this exists: closed-trade markers on the chart and the
 *  journal's Closed section both consume `trading.history`. History is
 *  REST (not part of the SSE stream) because each call is now a
 *  cheap server-side delta sync — the backend caches everything per
 *  the trading server account, so the API only ships the rows the client asks for.
 *
 *  Fetches once on owner-confirm and again whenever the open-
 *  positions count drops (a position closed → there's a new entry
 *  in history). No `days` filter — the cache is the source of
 *  truth and the journal shows all-time. */
export function useTradingHistoryAutoFetch(): void {
  const dispatch = useAppDispatch();
  const isOwner = useCan(CAP.tradingRead);
  const meStatus = useAppSelector((s) => s.me.status);
  const positionCount = useAppSelector((s) => s.trading.positions.length);
  const prevCountRef = useRef<number | null>(null);

  // Initial fetch on owner confirmation.
  useEffect(() => {
    if (meStatus !== "succeeded" || !isOwner) return;
    void dispatch(fetchTradingHistory());
  }, [dispatch, meStatus, isOwner]);

  // Refetch when the open-position count goes down — that's a
  // close event. Going up is a fresh open (no history impact).
  // Initial run with prev=null doesn't refetch (the effect above
  // already did).
  useEffect(() => {
    if (!isOwner) return;
    const prev = prevCountRef.current;
    prevCountRef.current = positionCount;
    if (prev === null) return;
    if (positionCount < prev) {
      void dispatch(fetchTradingHistory());
    }
  }, [dispatch, isOwner, positionCount]);
}
